import { ImgID } from "../../image_loader/img_id.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import { AbstractDialog } from "./abstract_dialog.js";
import { DialogId } from "./dialog_id.js";
import { DialogManager } from "./dialog_manager.js";
import { InAppReviewPlugin } from "../../plugins/in_app_review_plugin.js";

export class GoStoreDialog extends AbstractDialog
{
  public static readonly dialog_id = DialogId.GO_STORE_DIALOG;

  constructor()
  {
    const body_w = LayoutScaler.widthFromDesign( 906 );
    const body_h = LayoutScaler.heightFromDesign( 820 );

    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2;

    super( DialogManager.ctx, body_x, body_y, body_w, body_h );

    this.createElements();
  }

  private createElements(): void
  {
    this.createBackground();
    this.createTopIcon();
    this.createTitle();
    this.createContentText();
    this.createButtons();
    this.createCloseIcon();
  }

  private createBackground(): void
  {
    const bg = new UINineSliceElement(
      this.ctx,
      0,
      LayoutScaler.yFromDesign( 120 ),
      this.rect.getW(),
      LayoutScaler.heightFromDesign( 700 ),
      ImgID.UI_CapsuleBlue,
      "capsule_bg"
    );

    this.addChild( bg );
  }

  private createTopIcon(): void
  {
    const frame_w = LayoutScaler.widthFromDesign( 287 );
    const frame_h = LayoutScaler.heightFromDesign( 290 );
    const frame_x = ( this.rect.getW() - frame_w ) / 2;

    const frame = new UIImage(
      this.ctx,
      frame_x,
      LayoutScaler.yFromDesign( -5 ),
      frame_w,
      frame_h,
      ImgID.UI_RateDialogGameIconFrame,
    );
    this.addChild( frame );

    const top_icon_w = LayoutScaler.widthFromDesign( 210 );
    const top_icon_h = LayoutScaler.heightFromDesign( 210 );
    const top_icon_x = ( this.rect.getW() - top_icon_w ) / 2;
    const top_icon_y = LayoutScaler.yFromDesign( 35 );

    const top_icon = new UIImage(
      this.ctx,
      top_icon_x,
      top_icon_y,
      top_icon_w,
      top_icon_h,
      ImgID.UI_RateDialogGameIcon,
    );
    this.addChild( top_icon );
  }

  private createTitle(): void
  {
    const title_w = this.rect.getW();
    const title_h = LayoutScaler.heightFromDesign( 92 );
    const title_y = LayoutScaler.yFromDesign( 245 );

    const title = new UITextElement(
      this.ctx,
      0,
      title_y,
      title_w,
      title_h,
      {
        localization_key: "GO_STORE_DIALOG.TITLE",
        font_size: LayoutScaler.textSizeFromDesign( 60 ),
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        font_weight: 900,
        auto_wrap: false,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 12 ),
        outline_color: "#003F87",
        shadow_blur: LayoutScaler.widthFromDesign( 5 ),
        shadow_color: ColorScheme.black_transparent,
        shadow_offset_y: LayoutScaler.yFromDesign( 3 )
      }
    );
    this.addChild( title );
  }

  private createContentText(): void
  {
    const content_text_w = LayoutScaler.widthFromDesign( 760 );
    const content_text_h = LayoutScaler.heightFromDesign( 170 );
    const content_text_x = ( this.rect.getW() - content_text_w ) / 2;
    const content_text_y = LayoutScaler.yFromDesign( 350 );

    const content_text = new UITextElement(
      this.ctx,
      content_text_x,
      content_text_y,
      content_text_w,
      content_text_h,
      {
        localization_key: "GO_STORE_DIALOG.TEXT",
        font_size: LayoutScaler.textSizeFromDesign( 50 ),
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        font_weight: 900,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_color: "#003F87",
      }
    );
    this.addChild( content_text );
  }

  private createButtons(): void
  {
    const btn_w = LayoutScaler.widthFromDesign( 420 );
    const btn_h = LayoutScaler.heightFromDesign( 230 );
    const gap = LayoutScaler.widthFromDesign( 0 );
    const btn_y = LayoutScaler.yFromDesign( 550 );
    const start_x = ( this.rect.getW() - ( btn_w * 2 + gap ) ) / 2;

    const left_btn = new UINineSliceElement(
      this.ctx,
      start_x,
      btn_y,
      btn_w,
      btn_h,
      ImgID.UI_RateDialogBlueButton,
      "confirm_btn",
      undefined,
      undefined,
      true
    );

    const left_text = new UITextElement(
      this.ctx,
      0,
      LayoutScaler.yFromDesign( 6 ),
      btn_w,
      btn_h,
      {
        localization_key: "GO_STORE_DIALOG.CANCEL",
        font_size: LayoutScaler.textSizeFromDesign( 50 ),
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        ...TextStyleScheme.buttonShadow(),
        font_weight: 900,
        outline_color: ColorScheme.blue_btn_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_offset_y: LayoutScaler.yFromDesign( 2 ),
      }
    );
    left_btn.addChild( left_text, true );
    left_btn.setOnClick( () => this.close() );

    const right_btn = new UINineSliceElement(
      this.ctx,
      start_x + btn_w + gap,
      btn_y,
      btn_w,
      btn_h,
      ImgID.UI_GreenFrameBtn,
      "confirm_btn",
      undefined,
      undefined,
      true
    );

    const right_text = new UITextElement(
      this.ctx,
      0,
      LayoutScaler.yFromDesign( 6 ),
      btn_w,
      btn_h,
      {
        localization_key: "GO_STORE_DIALOG.PROCEED",
        font_size: LayoutScaler.textSizeFromDesign( 50 ),
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        ...TextStyleScheme.buttonShadow(),
        font_weight: 900,
        outline_color: ColorScheme.more_lives_buy_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_offset_y: LayoutScaler.yFromDesign( 2 ),
      }
    );
    right_btn.addChild( right_text, true );
    right_btn.setOnClick( () =>
    {
      this.close();
      void InAppReviewPlugin.requestReviewWithFallback();
    } );

    this.addChild( left_btn );
    this.addChild( right_btn );
  }

  private createCloseIcon(): void
  {
    const icon_w = LayoutScaler.widthFromDesign( 114 );
    const icon_h = LayoutScaler.heightFromDesign( 114 );
    const icon_x = this.rect.getW() - icon_w + LayoutScaler.xFromDesign( 30 );
    const icon_y = LayoutScaler.yFromDesign( 90 );

    const icon = new UIImage(
      this.ctx,
      icon_x,
      icon_y,
      icon_w,
      icon_h,
      ImgID.UI_SettingsDialogCloseBtn,
      {},
      true
    );

    icon.setOnClick( () => this.close() );

    this.addChild( icon );
  }

  public render(): void
  {
    this.startRender();
    this.finishRender();
  }

  public static open(): GoStoreDialog
  {
    return DialogManager.openPooled( "GoStoreDialog", undefined, () => new GoStoreDialog() ) as GoStoreDialog;
  }

  public static route(): GoStoreDialog
  {
    return DialogManager.routePooled( "GoStoreDialog", undefined, () => new GoStoreDialog() ) as GoStoreDialog;
  }

  public override isPoolable(): boolean { return true; }

  public name(): string
  {
    return "GoStoreDialog";
  }
}
